import React, { useState, useEffect } from 'react';
import {
  Webhook,
  Plus,
  Search,
  RefreshCw,
  Trash2,
  Edit,
  Play,
  Pause,
  Send,
  Eye,
  Check,
  X,
  AlertTriangle,
  Clock,
  Activity,
  Copy,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  RotateCcw,
} from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { webhookService } from 'api/services/developer.service';
import {
  WebhookDTO,
  WebhookLogDTO,
  CreateWebhookRequest,
  WebhookStatus,
  WebhookEventType,
  HttpMethod,
  WebhookStatsDTO,
  TestWebhookResponse,
} from 'types/developer';
import OrganizationFilter from 'components/Filters/OrganizationFilter';

// Event categories for organized display
const eventCategories = {
  'Call Events': [
    WebhookEventType.CALL_INITIATED,
    WebhookEventType.CALL_ANSWERED,
    WebhookEventType.CALL_COMPLETED,
    WebhookEventType.CALL_FAILED,
    WebhookEventType.CALL_TRANSFERRED,
  ],
  'SMS Events': [
    WebhookEventType.SMS_SENT,
    WebhookEventType.SMS_DELIVERED,
    WebhookEventType.SMS_FAILED,
    WebhookEventType.SMS_RECEIVED,
  ],
  'WhatsApp Events': [
    WebhookEventType.WHATSAPP_MESSAGE_SENT,
    WebhookEventType.WHATSAPP_MESSAGE_DELIVERED,
    WebhookEventType.WHATSAPP_MESSAGE_READ,
    WebhookEventType.WHATSAPP_MESSAGE_FAILED,
    WebhookEventType.WHATSAPP_MESSAGE_RECEIVED,
  ],
  'RCS Events': [
    WebhookEventType.RCS_MESSAGE_SENT,
    WebhookEventType.RCS_MESSAGE_DELIVERED,
    WebhookEventType.RCS_MESSAGE_READ,
    WebhookEventType.RCS_MESSAGE_FAILED,
  ],
  'Campaign Events': [
    WebhookEventType.CAMPAIGN_STARTED,
    WebhookEventType.CAMPAIGN_COMPLETED,
    WebhookEventType.CAMPAIGN_FAILED,
  ],
  'IVR Events': [
    WebhookEventType.IVR_SESSION_STARTED,
    WebhookEventType.IVR_SESSION_COMPLETED,
    WebhookEventType.IVR_DTMF_RECEIVED,
  ],
  'AI Agent Events': [
    WebhookEventType.AI_CONVERSATION_STARTED,
    WebhookEventType.AI_CONVERSATION_COMPLETED,
    WebhookEventType.AI_INTENT_DETECTED,
  ],
  'Billing Events': [
    WebhookEventType.CREDITS_LOW,
    WebhookEventType.CREDITS_EXHAUSTED,
    WebhookEventType.PAYMENT_RECEIVED,
    WebhookEventType.INVOICE_GENERATED,
  ],
};

const Webhooks: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const userRole = (currentOrganization?.role || user?.role || '').toLowerCase();
  const isMasterAdmin = userRole === 'master_admin';

  const [webhooks, setWebhooks] = useState<WebhookDTO[]>([]);
  const [stats, setStats] = useState<WebhookStatsDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [organizationId, setOrganizationId] = useState<string>(
    isMasterAdmin ? '' : currentOrganization?.id || ''
  );
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookDTO | null>(null);
  const [webhookLogs, setWebhookLogs] = useState<WebhookLogDTO[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [testResult, setTestResult] = useState<TestWebhookResponse | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateWebhookRequest>({
    name: '',
    url: '',
    method: HttpMethod.POST,
    events: [],
    headers: {},
    retryEnabled: true,
    maxRetries: 3,
    retryDelaySeconds: 60,
    timeoutSeconds: 30,
  });
  const [headerKey, setHeaderKey] = useState('');
  const [headerValue, setHeaderValue] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [testEventType, setTestEventType] = useState<WebhookEventType>(
    WebhookEventType.CALL_COMPLETED
  );

  useEffect(() => {
    fetchWebhooks();
    fetchStats();
  }, [organizationId, statusFilter, page]);

  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      const response = await webhookService.list({
        organizationId: organizationId || undefined,
        status: statusFilter || undefined,
        page,
        size: 10,
      });
      if (response.success && response.data) {
        setWebhooks(response.data.content || []);
        setTotalPages(response.data.totalPages || 1);
      } else {
        console.error('Failed to fetch webhooks:', response.error);
      }
    } catch (error) {
      console.error('Failed to fetch webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await webhookService.getStats(organizationId || undefined);
      if (response.success && response.data) {
        setStats(response.data);
      } else {
        console.error('Failed to fetch stats:', response.error);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchLogs = async (webhookId: string) => {
    try {
      setLogsLoading(true);
      const response = await webhookService.getLogs(webhookId, { size: 50 });
      if (response.success && response.data) {
        setWebhookLogs(response.data.content);
      } else {
        console.error('Failed to fetch logs:', response.error);
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      // The org and creator go as query params — the backend reads them there.
      const response = await webhookService.create(formData, {
        organizationId: organizationId || currentOrganization?.id,
        userId: user?.id ? String(user.id) : undefined,
      });
      if (response.success) {
        setShowCreateModal(false);
        fetchWebhooks();
        fetchStats();
        resetForm();
      } else {
        console.error('Failed to create webhook:', response.error);
      }
    } catch (error) {
      console.error('Failed to create webhook:', error);
    }
  };

  const handleUpdate = async () => {
    if (!selectedWebhook) return;
    try {
      const response = await webhookService.update(selectedWebhook.id, formData);
      if (response.success) {
        setShowEditModal(false);
        fetchWebhooks();
        resetForm();
      } else {
        console.error('Failed to update webhook:', response.error);
      }
    } catch (error) {
      console.error('Failed to update webhook:', error);
    }
  };

  const handleDelete = async () => {
    if (!selectedWebhook) return;
    try {
      const response = await webhookService.delete(selectedWebhook.id);
      if (response.success) {
        setShowDeleteModal(false);
        setSelectedWebhook(null);
        fetchWebhooks();
        fetchStats();
      } else {
        console.error('Failed to delete webhook:', response.error);
      }
    } catch (error) {
      console.error('Failed to delete webhook:', error);
    }
  };

  const handleToggleStatus = async (webhook: WebhookDTO) => {
    try {
      let response;
      if (webhook.status === WebhookStatus.ACTIVE) {
        response = await webhookService.disable(webhook.id);
      } else {
        response = await webhookService.enable(webhook.id);
      }
      if (response.success) {
        fetchWebhooks();
      } else {
        console.error('Failed to toggle webhook status:', response.error);
      }
    } catch (error) {
      console.error('Failed to toggle webhook status:', error);
    }
  };

  const handleTest = async () => {
    if (!selectedWebhook) return;
    try {
      setTestLoading(true);
      const response = await webhookService.test(selectedWebhook.id, {
        eventType: testEventType,
      });
      if (response.success && response.data) {
        setTestResult(response.data);
      } else {
        setTestResult({
          success: false,
          errorMessage: response.error || 'Failed to send test request',
        });
      }
    } catch (error) {
      console.error('Failed to test webhook:', error);
      setTestResult({
        success: false,
        errorMessage: 'Failed to send test request',
      });
    } finally {
      setTestLoading(false);
    }
  };

  const handleRetryLog = async (logId: string) => {
    if (!selectedWebhook) return;
    try {
      const response = await webhookService.retry(selectedWebhook.id, logId);
      if (response.success) {
        fetchLogs(selectedWebhook.id);
      } else {
        console.error('Failed to retry webhook:', response.error);
      }
    } catch (error) {
      console.error('Failed to retry webhook:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      method: HttpMethod.POST,
      events: [],
      headers: {},
      retryEnabled: true,
      maxRetries: 3,
      retryDelaySeconds: 60,
      timeoutSeconds: 30,
    });
    setHeaderKey('');
    setHeaderValue('');
    setSelectedWebhook(null);
    setExpandedCategories({});
  };

  const openEditModal = (webhook: WebhookDTO) => {
    setSelectedWebhook(webhook);
    setFormData({
      name: webhook.name,
      url: webhook.url,
      method: webhook.method,
      events: webhook.events,
      headers: webhook.headers || {},
      retryEnabled: webhook.retryEnabled,
      maxRetries: webhook.maxRetries,
      retryDelaySeconds: webhook.retryDelaySeconds,
      timeoutSeconds: webhook.timeoutSeconds,
    });
    setShowEditModal(true);
  };

  const openLogsModal = (webhook: WebhookDTO) => {
    setSelectedWebhook(webhook);
    setShowLogsModal(true);
    fetchLogs(webhook.id);
  };

  const openTestModal = (webhook: WebhookDTO) => {
    setSelectedWebhook(webhook);
    setTestResult(null);
    setShowTestModal(true);
  };

  const addHeader = () => {
    if (headerKey && headerValue) {
      setFormData({
        ...formData,
        headers: {
          ...formData.headers,
          [headerKey]: headerValue,
        },
      });
      setHeaderKey('');
      setHeaderValue('');
    }
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...formData.headers };
    delete newHeaders[key];
    setFormData({ ...formData, headers: newHeaders });
  };

  const toggleEvent = (event: WebhookEventType) => {
    if (formData.events.includes(event)) {
      setFormData({
        ...formData,
        events: formData.events.filter((e) => e !== event),
      });
    } else {
      setFormData({
        ...formData,
        events: [...formData.events, event],
      });
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories({
      ...expandedCategories,
      [category]: !expandedCategories[category],
    });
  };

  const selectAllInCategory = (events: WebhookEventType[]) => {
    const allSelected = events.every((e) => formData.events.includes(e));
    if (allSelected) {
      setFormData({
        ...formData,
        events: formData.events.filter((e) => !events.includes(e)),
      });
    } else {
      // Combine existing events with new ones, removing duplicates
      const combined = [...formData.events];
      events.forEach((event) => {
        if (!combined.includes(event)) {
          combined.push(event);
        }
      });
      setFormData({
        ...formData,
        events: combined,
      });
    }
  };

  const getStatusBadge = (status: WebhookStatus) => {
    const styles = {
      [WebhookStatus.ACTIVE]: 'bg-green-100 text-green-800',
      [WebhookStatus.INACTIVE]: 'bg-gray-100 text-gray-800',
      [WebhookStatus.FAILING]: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const filteredWebhooks = webhooks.filter((webhook) =>
    webhook.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Webhooks</h1>
          <p className="text-gray-600 dark:text-gray-400">Configure webhook endpoints for real-time event notifications</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Create Webhook
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Webhooks</p>
                <p className="text-2xl font-bold dark:text-white">{stats.totalWebhooks ?? 0}</p>
              </div>
              <Webhook className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.activeWebhooks ?? 0}</p>
              </div>
              <Check className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Deliveries (24h)</p>
                <p className="text-2xl font-bold dark:text-white">{(stats.deliveriesLast24h ?? 0).toLocaleString()}</p>
              </div>
              <Activity className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Failing</p>
                <p className="text-2xl font-bold text-red-600">{stats.failingWebhooks ?? 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search webhooks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>

          {isMasterAdmin && (
            <OrganizationFilter
              value={organizationId}
              onChange={setOrganizationId}
              allowServiceProviderSelection={true}
            />
          )}

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="FAILING">Failing</option>
          </select>

          <button
            onClick={() => {
              fetchWebhooks();
              fetchStats();
            }}
            className="p-2 border rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Webhooks Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Webhook
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Events
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Success Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Last Triggered
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
                  </td>
                </tr>
              ) : filteredWebhooks.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    No webhooks found
                  </td>
                </tr>
              ) : (
                filteredWebhooks.map((webhook) => {
                  const successRate =
                    webhook.successCount + webhook.failureCount > 0
                      ? Math.round(
                          (webhook.successCount / (webhook.successCount + webhook.failureCount)) * 100
                        )
                      : 100;

                  return (
                    <tr key={webhook.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{webhook.name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{webhook.url}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {webhook.method} | Timeout: {webhook.timeoutSeconds}s
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {webhook.events.slice(0, 3).map((event) => (
                            <span
                              key={event}
                              className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs dark:text-gray-300"
                            >
                              {event.replace(/_/g, ' ')}
                            </span>
                          ))}
                          {webhook.events.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs dark:text-gray-300">
                              +{webhook.events.length - 3} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(webhook.status)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden w-20">
                            <div
                              className={`h-full ${
                                successRate >= 90
                                  ? 'bg-green-500'
                                  : successRate >= 70
                                  ? 'bg-yellow-500'
                                  : 'bg-red-500'
                              }`}
                              style={{ width: `${successRate}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 dark:text-gray-400">{successRate}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {webhook.lastTriggeredAt
                          ? new Date(webhook.lastTriggeredAt).toLocaleString()
                          : 'Never'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openTestModal(webhook)}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title="Test"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openLogsModal(webhook)}
                            className="p-1 text-gray-400 hover:text-purple-600"
                            title="View Logs"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(webhook)}
                            className={`p-1 ${
                              webhook.status === WebhookStatus.ACTIVE
                                ? 'text-gray-400 hover:text-yellow-600'
                                : 'text-gray-400 hover:text-green-600'
                            }`}
                            title={webhook.status === WebhookStatus.ACTIVE ? 'Disable' : 'Enable'}
                          >
                            {webhook.status === WebhookStatus.ACTIVE ? (
                              <Pause className="w-4 h-4" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => openEditModal(webhook)}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedWebhook(webhook);
                              setShowDeleteModal(true);
                            }}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t flex justify-between items-center">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">
                {showCreateModal ? 'Create Webhook' : 'Edit Webhook'}
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="My Webhook"
                />
              </div>

              {/* URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endpoint URL *
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://your-server.com/webhook"
                />
              </div>

              {/* Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HTTP Method</label>
                <select
                  value={formData.method}
                  onChange={(e) =>
                    setFormData({ ...formData, method: e.target.value as HttpMethod })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value={HttpMethod.POST}>POST</option>
                  <option value={HttpMethod.PUT}>PUT</option>
                  <option value={HttpMethod.PATCH}>PATCH</option>
                </select>
              </div>

              {/* Events */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Events *</label>
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {Object.entries(eventCategories).map(([category, events]) => {
                    const allSelected = events.every((e) => formData.events.includes(e));
                    const someSelected = events.some((e) => formData.events.includes(e));

                    return (
                      <div key={category} className="border-b last:border-b-0">
                        <button
                          type="button"
                          onClick={() => toggleCategory(category)}
                          className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-2">
                            {expandedCategories[category] ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                            <span className="font-medium">{category}</span>
                            <span className="text-sm text-gray-500">
                              ({events.filter((e) => formData.events.includes(e)).length}/
                              {events.length})
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectAllInCategory(events);
                            }}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            {allSelected ? 'Deselect All' : 'Select All'}
                          </button>
                        </button>
                        {expandedCategories[category] && (
                          <div className="px-4 pb-2 space-y-1">
                            {events.map((event) => (
                              <label
                                key={event}
                                className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 px-2 rounded"
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.events.includes(event)}
                                  onChange={() => toggleEvent(event)}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">
                                  {event.replace(/_/g, ' ').toLowerCase()}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom Headers */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom Headers
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={headerKey}
                    onChange={(e) => setHeaderKey(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Header name"
                  />
                  <input
                    type="text"
                    value={headerValue}
                    onChange={(e) => setHeaderValue(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Header value"
                  />
                  <button
                    type="button"
                    onClick={addHeader}
                    className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Add
                  </button>
                </div>
                {formData.headers && Object.keys(formData.headers).length > 0 && (
                  <div className="space-y-1">
                    {Object.entries(formData.headers).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded"
                      >
                        <span className="text-sm">
                          <span className="font-medium">{key}:</span> {value}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeHeader(key)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Retry Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={formData.retryEnabled}
                      onChange={(e) =>
                        setFormData({ ...formData, retryEnabled: e.target.checked })
                      }
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700">Enable Retries</span>
                  </label>
                  {formData.retryEnabled && (
                    <div className="space-y-2">
                      <input
                        type="number"
                        value={formData.maxRetries}
                        onChange={(e) =>
                          setFormData({ ...formData, maxRetries: parseInt(e.target.value) || 3 })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Max retries"
                        min={1}
                        max={10}
                      />
                      <p className="text-xs text-gray-500">Max retries (1-10)</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timeout (seconds)
                  </label>
                  <input
                    type="number"
                    value={formData.timeoutSeconds}
                    onChange={(e) =>
                      setFormData({ ...formData, timeoutSeconds: parseInt(e.target.value) || 30 })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    min={5}
                    max={120}
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={showCreateModal ? handleCreate : handleUpdate}
                disabled={!formData.name || !formData.url || formData.events.length === 0}
                className="tl-btn tl-btn-primary disabled:opacity-50"
              >
                {showCreateModal ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      {showLogsModal && selectedWebhook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Webhook Logs</h2>
                <p className="text-sm text-gray-500">{selectedWebhook.name}</p>
              </div>
              <button
                onClick={() => {
                  setShowLogsModal(false);
                  setSelectedWebhook(null);
                  setWebhookLogs([]);
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {logsLoading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : webhookLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No delivery logs found</div>
              ) : (
                <div className="space-y-3">
                  {webhookLogs.map((log) => (
                    <div
                      key={log.id}
                      className={`border rounded-lg p-4 ${
                        log.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          {log.success ? (
                            <Check className="w-5 h-5 text-green-600" />
                          ) : (
                            <X className="w-5 h-5 text-red-600" />
                          )}
                          <span className="font-medium">
                            {log.eventType.replace(/_/g, ' ')}
                          </span>
                          {log.retryAttempt > 0 && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                              Retry #{log.retryAttempt}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">
                            {new Date(log.createdAt).toLocaleString()}
                          </span>
                          {!log.success && (
                            <button
                              onClick={() => handleRetryLog(log.id)}
                              className="p-1 text-blue-600 hover:text-blue-800"
                              title="Retry"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>
                          <span className="font-medium">Status:</span>{' '}
                          {log.responseStatus || 'N/A'}
                          {log.responseTimeMs && ` (${log.responseTimeMs}ms)`}
                        </p>
                        {log.errorMessage && (
                          <p className="text-red-600 mt-1">
                            <span className="font-medium">Error:</span> {log.errorMessage}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Test Modal */}
      {showTestModal && selectedWebhook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-lg p-6">
            <h2 className="text-xl font-bold mb-4">Test Webhook</h2>
            <p className="text-gray-600 mb-4">
              Send a test event to <strong>{selectedWebhook.name}</strong>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
              <select
                value={testEventType}
                onChange={(e) => setTestEventType(e.target.value as WebhookEventType)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {selectedWebhook.events.map((event) => (
                  <option key={event} value={event}>
                    {event.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>

            {testResult && (
              <div
                className={`p-4 rounded-lg mb-4 ${
                  testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {testResult.success ? (
                    <Check className="w-5 h-5 text-green-600" />
                  ) : (
                    <X className="w-5 h-5 text-red-600" />
                  )}
                  <span className="font-medium">
                    {testResult.success ? 'Success' : 'Failed'}
                  </span>
                </div>
                {testResult.responseStatus && (
                  <p className="text-sm">
                    Status: {testResult.responseStatus}
                    {testResult.responseTimeMs && ` (${testResult.responseTimeMs}ms)`}
                  </p>
                )}
                {testResult.errorMessage && (
                  <p className="text-sm text-red-600">{testResult.errorMessage}</p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setSelectedWebhook(null);
                  setTestResult(null);
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={handleTest}
                disabled={testLoading}
                className="tl-btn tl-btn-primary disabled:opacity-50"
              >
                {testLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Send Test
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedWebhook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Delete Webhook</h2>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedWebhook.name}"? You will stop receiving
              event notifications at this endpoint.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedWebhook(null);
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Webhooks;
